import { useRouter } from 'expo-router';
import { ScrollView } from 'react-native';
import { YStack } from 'tamagui';

import { LegalParagraph, LegalSection } from '@/components/legal-section';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { space } from '@/theme/tokens.generated';

// Issue #134 (part of the #159 Legal trust surfaces epic). See
// terms/index.tsx's own doc comment for the shared context on why this
// is a bundled in-app screen and why it's a founder-written draft, not
// law-firm-reviewed legal advice.
//
// Grounded in this app's actual, real data-handling behavior (checked
// against the live backend before writing this, not assumed): account
// deletion (`DELETE /me`) and data export (`GET /me/export`) are real,
// already-shipped API endpoints (backend/internal/http/router.go), so
// section 6 below can truthfully say deletion/export are available --
// but no in-app UI calls either endpoint yet (grepped frontend/src for
// both, zero call sites -- that's tracked separately as issue #87
// Settings / #158 GDPR completion), so this states the honest CURRENT
// path (contact us) rather than claiming a self-service button that
// doesn't exist. Revisit this section's wording once #87/#158 ship a
// real in-app control.
export function Privacy() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();

  return (
    <ScreenContainer scrollUnderDock>
      <YStack pt="$6" pb="$2">
        <ScreenHeader title="Privacy Policy" subtitle="Last updated September 15, 2026" onBack={() => router.back()} />
      </YStack>
      <ScrollView
        style={{ flex: 1 }}
        {...dockScroll}
        contentContainerStyle={{ paddingTop: space[2], paddingBottom: space[8] + dockClearance, gap: space[5] }}
      >
        <LegalSection title="1. What we collect">
          <LegalParagraph>
            Your account email, your handle, an optional bio, prompt answers, interests, and any photos you upload. Your
            precise location and search radius, which you grant when you use the app.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="2. How we use your location">
          <LegalParagraph>
            Your precise coordinates are stored on our servers only to determine which threads and people are within
            your radius — they are never shown to other users, and we never show your exact location to anyone,
            including in a &quot;this many feet away&quot; sense. Other users only ever see general distance.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="3. Photos and identity verification">
          <LegalParagraph>
            Your photos are hidden from other users until you and they mutually agree to a reveal. If you choose to
            verify your account with a selfie or ID photo, that image is deleted immediately after review — we only keep
            a record of whether you passed or failed, and when.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="4. Who we share data with">
          <LegalParagraph>
            We use Supabase for authentication, our database, and file storage. We don&apos;t sell your data or share it
            with advertisers or data brokers. We may disclose information if required by law.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="5. How long we keep data">
          <LegalParagraph>
            We keep your account data for as long as your account is active. If we ban an account for violating our
            terms, we retain a one-way cryptographic hash of the government ID used for verification (never the photo
            itself) so that person can&apos;t re-verify under a new account — this is the only data we keep specifically
            because of a ban.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="6. Your rights">
          <LegalParagraph>
            You can request a copy of your data or request that we delete your account and personal data at any time.
            Email us at privacy@nearkin.co to request either — we&apos;re working on a self-service option for both
            directly in the app.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="7. Children's privacy">
          <LegalParagraph>
            Nearkin is for people 18 and older. We don&apos;t knowingly collect data from anyone under 18.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="8. Security">
          <LegalParagraph>
            We use industry-standard practices to protect your data, including encrypted connections and access controls
            on our infrastructure. No system is perfectly secure, and we can&apos;t guarantee absolute security.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="9. Changes to this policy">
          <LegalParagraph>
            We may update this policy as the product changes. We&apos;ll update the date at the top of this page when we
            do.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title="10. Contact">
          <LegalParagraph>Questions about this policy? Reach us at privacy@nearkin.co.</LegalParagraph>
        </LegalSection>
      </ScrollView>
    </ScreenContainer>
  );
}
